import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { Badge, Card, ConfirmButton, JsonBlock, KeyValue, QueryBoundary } from '../components/ui';
import { useAccounts } from '../hooks/accounts';
import { atLeast } from '../lib/roles';
import { dateTime, num, signedMoney, titleCase } from '../lib/format';

export default function PositionDetail() {
  const { ticket } = useParams<{ ticket: string }>();
  const id = Number(ticket);
  const { user } = useAuth();
  const { selected } = useAccounts();
  const qc = useQueryClient();
  const [closeResult, setCloseResult] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['position-trace', id],
    queryFn: () => api.positionTrace(id),
    enabled: Number.isFinite(id),
  });

  const close = useMutation({
    mutationFn: (reason: string) =>
      api.closePosition(id, selected?.id as number, reason),
    onSuccess: (r) => {
      setCloseResult(r.detail);
      qc.invalidateQueries({ queryKey: ['position-trace', id] });
      qc.invalidateQueries({ queryKey: ['positions'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const t = q.data;
  const isOpen = t != null && t.position.close_time_utc === null;
  const brokerMode = selected?.trading_mode === 'demo' || selected?.trading_mode === 'live';
  const canClose = atLeast(user?.role, 'operator') && isOpen && brokerMode && selected != null;

  return (
    <>
      <PageHeader
        title={`Position ${ticket}`}
        description="Trade → execution → risk decision → signal → features (Phase 15)."
        actions={
          <div className="flex items-center gap-2">
            {canClose && (
              <ConfirmButton
                title={`Close position ${ticket} at the broker?`}
                body={
                  <>
                    This sends a real close to the broker for account #{selected?.id} (
                    {selected?.trading_mode}). It is audited and recorded on the position's event
                    trail. Existing SL/TP are removed with the position.
                  </>
                }
                confirmLabel="Close at broker"
                variant="danger"
                needsReason
                onConfirm={(reason) => close.mutateAsync(reason)}
              >
                Close position
              </ConfirmButton>
            )}
            <Link className="text-sm text-sky-400 hover:underline" to="/positions">
              ← all positions
            </Link>
          </div>
        }
      />

      {closeResult && (
        <Card className="mb-4 border-sky-900 bg-sky-950/40 px-4 py-3 text-sm text-sky-200">
          {closeResult}
        </Card>
      )}
      {isOpen && !brokerMode && selected != null && (
        <p className="mb-4 text-xs text-slate-500">
          Account #{selected.id} is in {selected.trading_mode} mode — there is no broker to close at.
        </p>
      )}

      <QueryBoundary q={q}>
        {t && (
          <div className="space-y-5">
            {t.notes.length > 0 && (
              <Card className="border-amber-800/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
                <ul className="list-disc space-y-1 pl-5">
                  {t.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </Card>
            )}

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">Position</h2>
              <KeyValue
                rows={[
                  ['Symbol', t.position.symbol],
                  ['Direction', <Badge tone={t.position.direction === 'long' ? 'good' : 'bad'}>{t.position.direction}</Badge>],
                  ['Volume', num(t.position.volume, 2)],
                  ['Open price', num(t.position.open_price, 5)],
                  ['Opened', dateTime(t.position.open_time_utc)],
                  ['Stop loss', num(t.position.stop_loss, 5)],
                  ['Initial stop', num(t.position.initial_stop_loss, 5)],
                  ['Take profit', num(t.position.take_profit, 5)],
                  ['Close price', num(t.position.close_price, 5)],
                  ['Closed', t.position.close_time_utc ? dateTime(t.position.close_time_utc) : 'open'],
                  ['Net P&L', signedMoney(t.position.net_profit)],
                  ['Magic', t.position.magic_number],
                  ['Execution id', t.position.execution_id ?? '—'],
                ]}
              />
            </Card>

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">Event trail</h2>
              {t.position.events.length === 0 ? (
                <p className="text-sm text-slate-500">No recorded events.</p>
              ) : (
                <ol className="space-y-2 text-sm">
                  {t.position.events.map((e, i) => (
                    <li key={i} className="flex flex-wrap gap-x-3 border-b border-edge/50 pb-2">
                      <span className="text-slate-500">{dateTime(e.at_utc)}</span>
                      <Badge>{titleCase(e.event_type)}</Badge>
                      <span className="text-slate-400">via {e.trigger}</span>
                      {e.detail && <span className="text-slate-300">{e.detail}</span>}
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">Execution</h2>
              {t.execution ? (
                <KeyValue
                  rows={[
                    ['State', <Badge tone="info">{t.execution.state}</Badge>],
                    ['Requested', num(t.execution.requested_volume, 2)],
                    ['Filled', num(t.execution.filled_volume, 2)],
                    ['Fill price', num(t.execution.fill_price, 5)],
                    ['Broker ticket', t.execution.broker_ticket ?? '—'],
                    ['Retcode', `${t.execution.retcode ?? '—'} (${t.execution.retcode_class ?? '—'})`],
                    ['Resolution attempts', t.execution.resolution_attempts],
                    ['Created', dateTime(t.execution.created_at_utc)],
                    ['Completed', t.execution.completed_at_utc ? dateTime(t.execution.completed_at_utc) : '—'],
                  ]}
                />
              ) : (
                <p className="text-sm text-slate-500">No execution row.</p>
              )}
              {t.execution && t.execution.transitions.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-slate-500">state transitions</summary>
                  <div className="mt-2">
                    <JsonBlock value={t.execution.transitions} />
                  </div>
                </details>
              )}
            </Card>

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">Risk decision</h2>
              {t.risk_decision ? (
                <>
                  <KeyValue
                    rows={[
                      [
                        'Status',
                        <Badge tone={t.risk_decision.status === 'approved' ? 'good' : 'bad'}>
                          {t.risk_decision.status}
                        </Badge>,
                      ],
                      ['Decided', dateTime(t.risk_decision.decided_at_utc)],
                      ['Volume', num(t.risk_decision.volume, 2)],
                      ['Entry', num(t.risk_decision.entry_price, 5)],
                      ['Stop', num(t.risk_decision.stop_loss, 5)],
                      ['Take profit', num(t.risk_decision.take_profit, 5)],
                      ['Risk amount', signedMoney(t.risk_decision.risk_amount, t.risk_decision.account_currency)],
                      ['Rejection reason', t.risk_decision.rejection_reason ?? '—'],
                    ]}
                  />
                  {t.risk_decision.checks.length > 0 && (
                    <details className="mt-3" open>
                      <summary className="cursor-pointer text-xs text-slate-500">
                        every check value ({t.risk_decision.checks.length})
                      </summary>
                      <div className="mt-2">
                        <JsonBlock value={t.risk_decision.checks} />
                      </div>
                    </details>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">No risk decision linked.</p>
              )}
            </Card>

            <Card className="p-4">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-300">
                Signal
                {t.signal && (
                  <Badge tone={t.signal.link === 'exact' ? 'good' : 'warn'}>{t.signal.link} link</Badge>
                )}
              </h2>
              {t.signal ? (
                <KeyValue
                  rows={[
                    ['Strategy', `${t.signal.strategy_id} ${t.signal.strategy_version}`],
                    ['Bar', dateTime(t.signal.bar_open_time_utc)],
                    ['Timeframe', t.signal.timeframe],
                    ['Direction', t.signal.direction ?? '—'],
                    ['Entry type', t.signal.entry_type ?? '—'],
                    ['Regime', t.signal.regime ?? '—'],
                    ['Feature set', t.signal.feature_set_version ?? '—'],
                    ['Reason', t.signal.reason],
                    ['Rationale', t.signal.rationale ?? '—'],
                  ]}
                />
              ) : (
                <p className="text-sm text-slate-500">No signal matched.</p>
              )}
            </Card>

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">Features at the entry bar</h2>
              {t.features ? (
                <>
                  <p className="mb-2 text-xs text-slate-500">
                    {t.features.feature_set_version} · computed {dateTime(t.features.computed_at)}
                  </p>
                  <JsonBlock value={t.features.values} />
                </>
              ) : (
                <p className="text-sm text-slate-500">Feature vector not available.</p>
              )}
            </Card>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
